
<template>
  <header class="dt-topbar">
    <div class="dt-left">
      <button
        type="button"
        class="dt-icon-button dt-menu-button"
        aria-label="Ouvrir le menu"
        @click="$emit('toggle-mobile')"
      >
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 7h16M4 12h16M4 17h16"/>
        </svg>
      </button>

      <button
        type="button"
        class="dt-icon-button dt-collapse-button"
        :aria-label="collapsed ? 'Développer le menu' : 'Réduire le menu'"
        @click="$emit('toggle-collapse')"
      >
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 5h16M4 12h16M4 19h16"/>
        </svg>
      </button>

      <div class="dt-page-heading">
        <div class="dt-breadcrumb">
          <router-link to="/admin">Administration</router-link>
          <span>/</span>
          <span>{{ pageTitle }}</span>
        </div>

        <h1>{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="dt-right">

      <!-- SEARCH -->
      <div ref="searchBox" class="dt-search">
        <svg class="dt-search-icon" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7"/>
          <path d="m16 16 5 5"/>
        </svg>

        <input
          v-model="search"
          type="search"
          placeholder="Rechercher une rubrique..."
          aria-label="Rechercher une rubrique"
          @focus="searchOpen = true"
          @keydown.enter.prevent="goToFirstResult"
          @keydown.esc="searchOpen = false"
        />

        <div
          v-if="searchOpen && search.trim()"
          class="dt-search-results"
        >
          <button
            v-for="item in searchResults"
            :key="item.path"
            type="button"
            @click="goTo(item.path)"
          >
            <span>{{ item.label }}</span>
            <span>→</span>
          </button>

          <p v-if="!searchResults.length">
            Aucune rubrique trouvée.
          </p>
        </div>
      </div>

      <!-- DATE -->
      <div class="dt-date">
        <svg viewBox="0 0 24 24" fill="none">
          <rect x="3" y="5" width="18" height="16" rx="2"/>
          <path d="M7 3v4M17 3v4M3 10h18"/>
        </svg>
        <span>{{ currentDate }}</span>
      </div>

      <span class="dt-separator"></span>

      <!-- PROFILE -->
      <div ref="profileBox" class="dt-profile-wrap">
        <button
          type="button"
          class="dt-profile-button"
          :aria-expanded="profileOpen ? 'true' : 'false'"
          aria-haspopup="menu"
          @click="profileOpen = !profileOpen"
        >
          <span class="dt-avatar">
            {{ userInitials }}
          </span>

          <span class="dt-profile-info">
            <strong>{{ userName }}</strong>
            <small>Administrateur</small>
          </span>

          <svg class="dt-chevron" viewBox="0 0 24 24" fill="none">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </button>

        <div v-if="profileOpen" class="dt-profile-menu" role="menu">
          <div class="dt-profile-menu-head">
            <strong>{{ userName }}</strong>
            <small>{{ userEmail || "Compte administrateur" }}</small>
          </div>

          <router-link
            to="/admin/settings"
            role="menuitem"
            @click.native="profileOpen = false"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2"/>
            </svg>
            Paramètres
          </router-link>

          <router-link
            to="/"
            role="menuitem"
            @click.native="profileOpen = false"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9"/>
              <path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/>
            </svg>
            Voir le site
          </router-link>

          <button
            type="button"
            class="dt-logout"
            role="menuitem"
            @click="requestLogout"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M10 17l5-5-5-5M15 12H3M12 3h6a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3h-6"/>
            </svg>
            Se déconnecter
          </button>
        </div>
      </div>

    </div>
  </header>
</template>

<script>
export default {
  name: "DashboardTopbar",

  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },

    user: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      search: "",
      searchOpen: false,
      profileOpen: false,

      sections: [
        { label: "Tableau de bord", path: "/admin" },
        { label: "Commandes", path: "/admin/orders" },
        { label: "Produits", path: "/admin/products" },
        { label: "Clients", path: "/admin/customers" },
        { label: "Garages partenaires", path: "/admin/garages" },
        { label: "Rendez-vous", path: "/admin/appointments" },
        { label: "Livraisons", path: "/admin/deliveries" },
        { label: "Promotions", path: "/admin/promotions" },
        { label: "Rapports", path: "/admin/reports" },
        { label: "Paramètres", path: "/admin/settings" },
      ],
    };
  },

  computed: {
    pageTitle() {
      const titles = {
        "/admin": "Tableau de bord",
        "/admin/orders": "Commandes",
        "/admin/products": "Produits",
        "/admin/customers": "Clients",
        "/admin/garages": "Garages partenaires",
        "/admin/appointments": "Rendez-vous",
        "/admin/deliveries": "Livraisons",
        "/admin/promotions": "Promotions",
        "/admin/reports": "Rapports",
        "/admin/settings": "Paramètres",
      };

      const path = this.$route.path;

      if (titles[path]) {
        return titles[path];
      }

      const parent = Object.keys(titles)
        .filter(key => key !== "/admin")
        .find(key => path.indexOf(key + "/") === 0);

      return parent ? titles[parent] : "Administration";
    },

    userName() {
      return this.user.name ||
        [this.user.first_name, this.user.last_name]
          .filter(Boolean)
          .join(" ") ||
        "Administrateur";
    },

    userInitials() {
      return this.userName
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0))
        .join("")
        .toUpperCase();
    },

    userEmail() {
      return this.user.email || "";
    },

    currentDate() {
      return new Intl.DateTimeFormat("fr-FR", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date());
    },

    searchResults() {
      const q = this.search.trim().toLowerCase();

      if (!q) return [];

      return this.sections.filter(item =>
        item.label.toLowerCase().includes(q)
      );
    },
  },

  watch: {
    "$route.path"() {
      this.profileOpen = false;
      this.searchOpen = false;
      this.search = "";
    },
  },

  mounted() {
    document.addEventListener("mousedown", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleEscape);
  },

  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleEscape);
  },

  methods: {
    handleOutsideClick(event) {
      const profile = this.$refs.profileBox;
      const search = this.$refs.searchBox;

      if (profile && !profile.contains(event.target)) {
        this.profileOpen = false;
      }

      if (search && !search.contains(event.target)) {
        this.searchOpen = false;
      }
    },

    handleEscape(event) {
      if (event.key === "Escape") {
        this.profileOpen = false;
        this.searchOpen = false;
      }
    },

    goTo(path) {
      this.searchOpen = false;
      this.search = "";

      if (path !== this.$route.path) {
        this.$router.push(path).catch(error => {
          if (error.name !== "NavigationDuplicated") {
            console.error(error);
          }
        });
      }
    },

    goToFirstResult() {
      if (this.searchResults.length) {
        this.goTo(this.searchResults[0].path);
      }
    },

    requestLogout() {
      this.profileOpen = false;
      this.$emit("logout");
    },
  },
};
</script>

<style scoped>
.dt-topbar {
  position: sticky;
  top: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 78px;
  padding: 14px 30px;
  background: #fff;
  border-bottom: 1px solid #e8ecf0;
  font-family: "Poppins", sans-serif;
}

.dt-topbar *,
.dt-topbar *::before,
.dt-topbar *::after {
  box-sizing: border-box;
}

.dt-topbar svg {
  width: 18px;
  height: 18px;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.dt-left,
.dt-right {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}

.dt-right {
  margin-left: auto;
}

.dt-icon-button {
  display: grid;
  place-items: center;
  width: 39px;
  height: 39px;
  flex-shrink: 0;
  background: #f5f7f9;
  color: #59636e;
  border: 1px solid #edf0f3;
  border-radius: 9px;
  cursor: pointer;
}

.dt-icon-button:hover {
  background: #fff0e5;
  color: #ff7900;
}

.dt-menu-button {
  display: none;
}

/* PAGE HEADING */

.dt-page-heading {
  min-width: 0;
}

.dt-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
  font-size: 10px;
  color: #9ba4ae;
}

.dt-breadcrumb a {
  color: #9ba4ae;
  text-decoration: none;
}

.dt-breadcrumb a:hover {
  color: #ff7900;
}

.dt-page-heading h1 {
  color: #20262d;
  font-size: 19px;
  font-weight: 900;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* SEARCH */

.dt-search {
  position: relative;
  display: flex;
  align-items: center;
  width: 245px;
  height: 39px;
}

.dt-search-icon {
  position: absolute;
  left: 13px;
  color: #98a2ab;
  pointer-events: none;
}

.dt-search input {
  width: 100%;
  height: 100%;
  padding: 0 12px 0 40px;
  border: 1px solid #e8edf0;
  outline: none;
  background: #f7f9fa;
  border-radius: 9px;
  color: #28313a;
  font-size: 11px;
}

.dt-search input:focus {
  border-color: #ff7900;
  background: #fff;
}

.dt-search input::placeholder {
  color: #a3abb3;
}

.dt-search-results {
  position: absolute;
  top: calc(100% + 9px);
  left: 0;
  right: 0;
  padding: 7px;
  border-radius: 10px;
  border: 1px solid #e9edf0;
  background: #fff;
  box-shadow: 0 14px 35px rgba(22,29,36,.12);
  z-index: 100;
}

.dt-search-results button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px;
  border: 0;
  border-radius: 6px;
  background: none;
  color: #39424b;
  font-size: 11px;
  text-align: left;
  cursor: pointer;
}

.dt-search-results button:hover {
  background: #fff0e5;
  color: #e96e00;
}

.dt-search-results p {
  margin: 0;
  padding: 10px;
  font-size: 11px;
  color: #89939c;
}

/* DATE */

.dt-date {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #89939c;
  font-size: 10px;
  white-space: nowrap;
}

.dt-date svg {
  color: #ff7900;
}

.dt-separator {
  width: 1px;
  height: 33px;
  background: #e8ecf0;
}

/* PROFILE */

.dt-profile-wrap {
  position: relative;
}

.dt-profile-button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;
}

.dt-avatar {
  display: grid;
  place-items: center;
  width: 37px;
  height: 37px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #ff7900;
  color: #fff;
  font-size: 12px;
  font-weight: 900;
}

.dt-profile-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  max-width: 135px;
}

.dt-profile-info strong {
  color: #303840;
  font-size: 11px;
  font-weight: 850;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dt-profile-info small {
  color: #9ca5ae;
  font-size: 10px;
}

.dt-chevron {
  width: 14px !important;
  height: 14px !important;
  color: #a1aab2;
}

.dt-profile-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 14px);
  width: 245px;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  padding: 7px;
  background: white;
  box-shadow: 0 18px 45px rgba(20,27,34,.14);
  z-index: 200;
}

.dt-profile-menu-head {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 13px;
  border-bottom: 1px solid #edf0f2;
  margin-bottom: 6px;
  overflow: hidden;
}

.dt-profile-menu-head strong {
  color: #29323a;
  font-size: 12px;
}

.dt-profile-menu-head small {
  color: #98a2aa;
  font-size: 10px;
  overflow-wrap: anywhere;
}

.dt-profile-menu > a,
.dt-profile-menu > button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 39px;
  padding: 10px;
  border: 0;
  border-radius: 7px;
  background: none;
  color: #5e6872;
  font-size: 11px;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.dt-profile-menu > a:hover,
.dt-profile-menu > button:hover {
  background: #f5f7f9;
  color: #ff7900;
}

.dt-profile-menu .dt-logout {
  color: #c34f42;
  border-top: 1px solid #edf0f2;
  margin-top: 5px;
  border-radius: 0 0 7px 7px;
}

/* RESPONSIVE */

@media (max-width: 1250px) {
  .dt-search {
    width: 195px;
  }

  .dt-date {
    display: none;
  }
}

@media (max-width: 991px) {
  .dt-topbar {
    min-height: 70px;
    padding: 12px 19px;
  }

  .dt-menu-button {
    display: grid;
  }

  .dt-collapse-button {
    display: none;
  }

  .dt-right,
  .dt-left {
    gap: 12px;
  }

  .dt-search {
    width: 205px;
  }
}

@media (max-width: 650px) {
  .dt-topbar {
    padding: 10px 14px;
  }

  .dt-search,
  .dt-separator,
  .dt-profile-info,
  .dt-chevron {
    display: none;
  }

  .dt-page-heading h1 {
    font-size: 15px;
  }

  .dt-breadcrumb {
    font-size: 9px;
  }

  .dt-avatar {
    width: 35px;
    height: 35px;
  }

  .dt-profile-menu {
    right: 0;
    width: min(245px, calc(100vw - 28px));
  }
}

@media (prefers-reduced-motion: reduce) {
  .dt-topbar * {
    transition: none !important;
  }
}
</style>
